import { useEffect, useState } from "react";

import { ViewType } from "@app/redux/reducers/settings";
import { SHOW_AD_EVERY_LINES } from "@app/constants/overviews-numbers";
import { FilterItem } from "@app/api/core/filter/filter-item";
import { IOutFeaturedMerchantDTO } from "@app/core/new-merchant-card/featured-merchants.interface";
import { DEFAULT_MERCHANT_MAX } from "@app/components/merchant-slider/merchant-slider";
import { CtaCard } from "@app/api/core/cta-card";

import { IExtendedBannerDTO, saveBannerSeenInLocalStorage } from "./local-storage-banners";

interface IOverviewItem<T> {
  id: string;
  banner?: IExtendedBannerDTO;
  item?: T;
  merchantSlider?: { merchants: IOutFeaturedMerchantDTO[]; startIndex: number };
  ctaCard?: CtaCard;
}

const getOverviewItems = <T extends Record<string, any>>(
  viewType: ViewType,
  _items: T[],
  banners: IExtendedBannerDTO[],
  sliderMerchants?: IOutFeaturedMerchantDTO[],
  ctaCards?: CtaCard[]
): IOverviewItem<T>[] => {
  bannerIndex = 0;
  const overviewItemsResult: IOverviewItem<T>[] = [];
  const showAdEvery = getAmountOfAdsPerItems(viewType);
  const showMerchantSlider = sliderMerchants && sliderMerchants.length > 0;
  const items = putCtaCardsInOverviewItems(_items, ctaCards);

  let showAlternativeBanner = false;
  let nextIsSliderMerchant = showMerchantSlider ? banners.length === 0 : false;
  let sliderMerchantsIndex = 0;

  items.forEach(({ id, item, ctaCard }, index) => {
    const showAd = (index + 1) % showAdEvery === 0;

    if (showAd && (banners.length > 0 || nextIsSliderMerchant)) {
      if (nextIsSliderMerchant && showMerchantSlider) {
        overviewItemsResult.push({
          id,
          item,
          ctaCard,
          merchantSlider: {
            merchants: sliderMerchants.slice(0, DEFAULT_MERCHANT_MAX),
            startIndex: sliderMerchantsIndex
          }
        });
        nextIsSliderMerchant = banners.length === 0;
        sliderMerchantsIndex += 1;
      } else if (banners.length > 0) {
        overviewItemsResult.push({
          id,
          item,
          ctaCard,
          banner: { ...getNextBanner(banners), showAlternativeBanner }
        });
        showAlternativeBanner = !showAlternativeBanner;

        if (showMerchantSlider) {
          nextIsSliderMerchant = true;
        }
      }
    } else {
      const banner = items.length < showAdEvery &&
        banners.length > 0 &&
        showAd && { ...getNextBanner(banners), showAlternativeBanner };
      overviewItemsResult.push({
        id,
        item,
        ctaCard,
        ...(banner && { banner })
      });
    }
  });

  return overviewItemsResult;
};

const calculateTotalResultsWithCtaCards = (totalResults: number, ctaCards?: CtaCard[]): number => {
  const ctaPositions = calculateCtaPositions(totalResults, ctaCards);

  let itemIndex = 0;
  let currentPosition = 1;
  let totalItems = 0;

  while (itemIndex < totalResults || ctaPositions.has(currentPosition)) {
    if (ctaPositions.has(currentPosition)) {
      totalItems++;
    } else if (itemIndex < totalResults) {
      totalItems++;
      itemIndex++;
    }
    currentPosition++;
  }

  return totalItems;
};

const putCtaCardsInOverviewItems = <T extends Record<string, any>>(
  items: T[],
  ctaCards?: CtaCard[]
): IOverviewItem<T>[] => {
  if (!ctaCards || ctaCards.length === 0) {
    return items.map((item) => ({ id: item.id, item }));
  }

  // First, create a map of all CTA positions
  const ctaPositions = calculateCtaPositions(items.length, ctaCards);

  // Now build the final array by iterating through positions
  const overviewItems: IOverviewItem<T>[] = [];
  let itemIndex = 0;
  let currentPosition = 1;

  // Continue until we've processed all items and potential CTAs
  while (itemIndex < items.length || ctaPositions.has(currentPosition)) {
    // Check if there's a CTA at this position
    if (ctaPositions.has(currentPosition)) {
      const cta = ctaPositions.get(currentPosition);
      if (cta) {
        overviewItems.push({
          id: `cta-${currentPosition}`,
          ctaCard: cta.ctaCard
        });
      }
    } else if (itemIndex < items.length) {
      // Only add an item if there's no CTA at this position AND we have items left
      const overviewItem = items[itemIndex];
      overviewItems.push({
        id: overviewItem.id,
        item: overviewItem
      });
      itemIndex++;
    }

    currentPosition++;
  }

  return overviewItems;
};

const calculateCtaPositions = (totalResults: number, ctaCards?: CtaCard[]) => {
  const ctaPositions = new Map<number, { ctaCard: CtaCard; isFixed?: boolean }>();

  if (!ctaCards || ctaCards.length === 0) {
    return ctaPositions;
  }

  // Reversing so the items earlier have priority
  [...ctaCards].reverse().forEach((ctaCard) => {
    const positions =
      ctaCard?.positions
        ?.split(";")
        .map((pos) => Number(pos.trim()))
        .filter((x) => !Number.isNaN(x)) || [];

    if (positions.length === 0) return;

    if (ctaCard.repeater) {
      const interval = positions[0];
      for (let pos = interval; pos <= totalResults * 2; pos += interval) {
        if (!ctaPositions.has(pos) || !ctaPositions.get(pos)?.isFixed) {
          ctaPositions.set(pos, { ctaCard });
        }
      }
    } else {
      positions.forEach((pos) => {
        ctaPositions.set(pos, { ctaCard, isFixed: true });
      });
    }
  });

  return ctaPositions;
};

let bannerIndex = 0;
const getNextBanner = (banners: IExtendedBannerDTO[]): IExtendedBannerDTO => {
  if (bannerIndex >= banners.length) {
    bannerIndex = 0;
  }
  const result = banners[bannerIndex];

  saveBannerSeenInLocalStorage(result.id);

  bannerIndex += 1;

  return result;
};

const getAmountOfAdsPerItems = (viewType: ViewType) => {
  switch (viewType) {
    case ViewType.DesktopFull:
      return SHOW_AD_EVERY_LINES * 5;

    case ViewType.DesktopLarge:
      return SHOW_AD_EVERY_LINES * 4;

    case ViewType.Desktop:
      return SHOW_AD_EVERY_LINES * 3;

    case ViewType.Mobile:
    case ViewType.MobileBig:
    case ViewType.Tablet:
      return SHOW_AD_EVERY_LINES * 2;
    default:
      return 0;
  }
};

const useGetTotalHeight = (results: number, totalResults: number, overviewHeight?: number) => {
  const [totalHeight, setTotalHeight] = useState(getTotalHeight(results, totalResults, overviewHeight));

  useEffect(() => {
    if (overviewHeight) {
      const newHeight = getTotalHeight(results, totalResults, overviewHeight);

      if (newHeight > totalHeight) {
        setTotalHeight(newHeight);
      }
    }
  }, [results, totalResults, overviewHeight]);

  return totalHeight;
};

const getTotalHeight = (results: number, totalResults: number, overviewHeight?: number) => {
  if (!overviewHeight) {
    return 0;
  }

  const heightPerResult = overviewHeight / results;
  const calculatedHeight = heightPerResult * totalResults;

  return overviewHeight > calculatedHeight ? overviewHeight : calculatedHeight;
};

const allFiltersAndItemsAreEmpty = <T extends Record<string, any>>(
  allFilterItems: FilterItem[][],
  items: T[],
  excludedItems?: boolean
) => {
  const totalSelecteditems = allFilterItems.reduce(
    (count, filterItems) => count + filterItems.filter((item) => item.isSelected).length,
    0
  );

  if (totalSelecteditems > 0) {
    return false;
  }

  if (excludedItems || items.length === 0) {
    return true;
  }

  return false;
};

const singleFiltersAreDifferent = (oldFilters: FilterItem[], newFilters: FilterItem[]) => {
  for (let i = 0; i < newFilters.length; i += 1) {
    if (!oldFilters[i]) {
      return false;
    }
    if (oldFilters[i].isSelected !== newFilters[i].isSelected) {
      return true;
    }
  }

  return false;
};

const getIfSticky = (
  filterBarEntry?: IntersectionObserverEntry | null,
  bottomEntry?: IntersectionObserverEntry | null,
  totalResults?: number,
  totalItems?: number
) => {
  if (
    bottomEntry?.boundingClientRect.bottom &&
    bottomEntry.boundingClientRect.bottom <= 0 &&
    totalResults === totalItems
  ) {
    return false;
  }

  if (filterBarEntry?.boundingClientRect.top && filterBarEntry.boundingClientRect.top <= 0) {
    return true;
  }

  return false;
};

export {
  IOverviewItem,
  getOverviewItems,
  getTotalHeight,
  allFiltersAndItemsAreEmpty,
  singleFiltersAreDifferent,
  getIfSticky,
  useGetTotalHeight,
  calculateTotalResultsWithCtaCards
};
